import { useCallback, useState } from 'react';
import {
  createGetAllAPI,
  createPostAPI,
  createPatchAPI,
  RRRFetch,
} from '@cuemath/rest-api';
import { BASE_URL } from 'constants/api';

const HOF = `${BASE_URL}/hof`;

// List records: getAll({ status, tier, limit, offset, includeCount })
//
// `tier` separates achievements (HOF) from impact stories (STORY) and from the rows judged
// not to belong (NONE). It is a filter of its own rather than part of `status`, because
// STORY and NONE share the one `logged` status.
//
// `includeCount` changes the response shape — with it the service returns
// `{ items, count }` instead of a bare array, because a list cannot carry a total and the
// table needs one for its page numbers. Both shapes are handled at the call site.
//
// Only non-empty values are appended, so `getAll({})` still requests the whole unpaginated
// list exactly as before: absent `limit`/`offset` mean "everything" on the service side.
//
// `status` takes one value or an array. The service splits on the comma and 400s on any
// unknown member, so a merged tile is one request that can still be paged and counted as
// one — two requests could be neither.
const recordsQuery = query => {
  const { status, tier, limit, offset, includeCount } = query || {};
  const params = [];

  // One status or several: a queue tile is not always one status, and "Needs a decision"
  // asks for `under_review` and `parked` together. Joined explicitly rather than letting
  // an array stringify itself, and encoded per value so the separator stays a separator.
  const statuses = Array.isArray(status) ? status.filter(Boolean) : [status].filter(Boolean);

  if (statuses.length) {
    params.push(`status=${statuses.map(encodeURIComponent).join(',')}`);
  }
  if (tier) params.push(`tier=${encodeURIComponent(tier)}`);
  // `offset=0` is falsy but meaningful, so test for null/undefined rather than truthiness.
  if (limit !== undefined && limit !== null) params.push(`limit=${limit}`);
  if (offset !== undefined && offset !== null) params.push(`offset=${offset}`);
  if (includeCount) params.push('include_count=true');

  return params.length ? `?${params.join('&')}` : '';
};

const { useGetAll: useGetHoFRecords, invalidate: invalidateHoFRecords } = createGetAllAPI(
  {
    getURL: query => `${HOF}/records/${recordsQuery(query)}`,
  },
);

// Approved exam explainers, used to populate the achievement form's exam picker
// so an examKey can only be chosen from the library (a typo would otherwise
// park the record). The gateway proxies /hof/<path:subpath>, so no BE change.
const { useGetAll: useGetHoFLibrary } = createGetAllAPI({
  getURL: () => `${HOF}/library/`,
});

// How many records sit in each queue, for the tiles that are the screen's filter. Takes the
// queue descriptors themselves, so a queue is counted by whatever filter it navigates to.
//
// One request per queue, in parallel: `include_count` reports the total for a single filter,
// and the service has no aggregate endpoint. `limit=1` keeps each response to one row, so
// this costs one count per tile rather than one full list per tile. `status` is indexed and
// `tier` is not, so the two logged tiles narrow on status first — cheap for as long as
// `logged` stays a small slice of the table, and the thing to index if it stops being one.
//
// Keyed by queue key rather than by status, because the two `logged` queues differ only by
// tier — keying on status would collapse them back into the single number that hid the
// difference in the first place.
//
// A queue whose request fails is left null rather than defaulted to 0 — a tile reading zero
// when there is work waiting is worse than a tile reading nothing.
const useHoFQueueCounts = () => {
  const [counts, setCounts] = useState({});

  const fetchCounts = useCallback(async queues => {
    const settled = await Promise.all(
      queues.map(async ({ key, query }) => {
        const url = `${HOF}/records/${recordsQuery({
          ...query,
          limit: 1,
          offset: 0,
          includeCount: true,
        })}`;
        try {
          const { data } = await RRRFetch.fetch(url, { method: 'GET' });
          const count = data && typeof data.count === 'number' ? data.count : null;
          return [key, count];
        } catch (e) {
          return [key, null];
        }
      }),
    );
    setCounts(settled.reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {}));
  }, []);

  return { counts, fetchCounts };
};

// Run the agent over draft/parked rows: post({})
const { usePost: useAssembleHoF } = createPostAPI({
  getURL: () => `${HOF}/assemble/`,
});

// Lifecycle: post({}, recordId) — for POST, getURL receives (payload, ...extraArgs).
const { usePost: usePublishHoF } = createPostAPI({
  getURL: (_, id) => `${HOF}/records/${id}/publish/`,
});
// Triage: post({ tier, explainer?, category? }, recordId) — the moderator's verdict on
// where a record belongs. Its own endpoint, not a PATCH: the row it acts on usually has no
// assembled record, and the edit endpoint refuses those.
const { usePost: useTriageHoF } = createPostAPI({
  getURL: (_, id) => `${HOF}/records/${id}/triage/`,
});
// Sanitise: post({ examName?, result? }, recordId) — admin-only correction of the one
// sentence a custom submission arrives with, which otherwise reaches every surface as both
// the exam name and the result.
const { usePost: useSanitiseHoF } = createPostAPI({
  getURL: (_, id) => `${HOF}/records/${id}/sanitise/`,
});
const { usePost: useUnpublishHoF } = createPostAPI({
  getURL: (_, id) => `${HOF}/records/${id}/unpublish/`,
});
const { usePost: useRemoveHoF } = createPostAPI({
  getURL: (_, id) => `${HOF}/records/${id}/remove/`,
});

// Edit assembled record fields: patch(recordId, partialRecord) — id first for PATCH.
const { usePatch: useEditHoF } = createPatchAPI({
  getURL: id => `${HOF}/records/${id}/`,
});

// Upload a replacement photo for one record. webbackend re-hosts it: strips EXIF (a phone
// photo carries GPS, i.e. where the child lives), stores it privately, and only makes it
// public on publish.
//
// Not createPostAPI: that JSON-encodes the body. A multipart upload must go out as
// FormData with NO Content-Type header, so the browser can set the boundary — supplying
// one ourselves produces a body the far end cannot parse.
const useUploadHoFPhoto = () => {
  const [isProcessing, setIsProcessing] = useState(false);

  const upload = useCallback(async (recordId, file) => {
    setIsProcessing(true);
    try {
      const body = new FormData();
      body.append('photo', file);
      const { data } = await RRRFetch.fetch(`${HOF}/records/${recordId}/photo/`, {
        method: 'POST',
        body,
      });
      return { ok: true, data };
    } catch (e) {
      const { code, error } = e || {};
      // nginx caps request bodies at 1 MB and answers with an HTML 413, which has no
      // `message` to read — without this the admin just sees "Photo upload failed".
      if (code === 413) {
        return {
          ok: false,
          message:
            'That image was too large for the server to accept. Try a smaller photo.',
        };
      }
      return { ok: false, message: (error && error.message) || 'Photo upload failed' };
    } finally {
      setIsProcessing(false);
    }
  }, []);

  return { upload, isProcessing };
};

export {
  useGetHoFRecords,
  invalidateHoFRecords,
  useHoFQueueCounts,
  useGetHoFLibrary,
  useAssembleHoF,
  usePublishHoF,
  useTriageHoF,
  useSanitiseHoF,
  useUnpublishHoF,
  useRemoveHoF,
  useEditHoF,
  useUploadHoFPhoto,
};
